import { LinkedinSettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import { LinkedinRecommendationKey, StageType } from '$extensionSrc/utils/enums'

import { StageDef } from '../automationMachineInterfaces'
import { evaluateXpath } from '../interruptibleXpath'
import {
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { getElement, getOptionalElement } from '../utils'
import { LINKEDIN_AUTOMATION_STAGE_STATUSES } from './linkedinAutomationStageStatuses'
import {
  LinkedinShowProfilePhotoError,
  LinkedinShowProfilePhotoErrorName,
} from './linkedinShowProfilePhotoError'
import { homeFeedUrl, profileSettingsUrl } from './linkedinUrls'

type SettingValue = 'CONNECTIONS' | 'NETWORK' | 'LINKEDIN_USER' | 'PUBLIC'

const TARGET_VALUE: SettingValue = 'NETWORK'

const mapSettingValuesToLabels: Record<SettingValue, string> = {
  CONNECTIONS: 'Only First Degree Connections',
  NETWORK: 'Your Network',
  LINKEDIN_USER: 'All LinkedIn members',
  PUBLIC: 'Public',
}

/**
 * Returns true if a is as strict or stricter than b.
 *
 * While we require inputs are of type "setting value" we also handle unknowns.
 */
function asStrictOrStricterThan(a: SettingValue | string, b: SettingValue) {
  // Lower values are stricter
  function _getRank(x: SettingValue | string) {
    if (x === 'CONNECTIONS') {
      return 1
    } else if (x === 'NETWORK') {
      return 2
    } else if (x === 'LINKEDIN_USER') {
      return 3
    } else if (x === 'PUBLIC') {
      return 4
    }
    // TODO: LOG THAT WE FOUND AN UNRECOGNIZED VALUE
    return 5
  }

  return _getRank(a) <= _getRank(b)
}

/**
 * Look for an <img/> tag with src "media.licdn" in the "picture" section.
 *
 * Profiles without profile photos will only have the default profile photo, which is hosted at static.licdn
 */
function hasProfilePhoto(): boolean {
  const candidates = evaluateXpath<HTMLImageElement>(
    document,
    '//*[@data-section = "picture"]/descendant::img[contains(@src, "media.licdn")]',
  )

  return candidates.length > 0
}

/**
 * Check the profile page for the parent form of the profile photo visibility setting
 */
function hasProfilePublicVisibilityForm(): boolean {
  const candidates = evaluateXpath<HTMLFormElement>(
    document,
    '//form[@class = "controls-form"]',
  )

  return candidates.length > 0
}

async function getCurrentPhotoVisibilitySelection(): Promise<SettingValue> {
  const selectedOption = await getOptionalElement<HTMLInputElement>(
    document,
    '//input[@name="pictureVisibilityLevel" and @checked]',
    { timeout: 2000 },
  )
  if (!selectedOption) {
    if (!hasProfilePublicVisibilityForm()) {
      throw new LinkedinShowProfilePhotoError(
        'No profile public visibility form found',
        LinkedinShowProfilePhotoErrorName.NO_FORM,
      )
    } else if (!hasProfilePhoto()) {
      throw new LinkedinShowProfilePhotoError(
        'No profile picture',
        LinkedinShowProfilePhotoErrorName.NO_PROFILE_PHOTO,
      )
    } else {
      throw new Error('No photo visibility selection found')
    }
  }
  return selectedOption.value as unknown as SettingValue
}

async function setPhotoVisibilitySelection(value: SettingValue): Promise<void> {
  const desiredOption = await getElement<HTMLInputElement>(
    document,
    `//input[@name="pictureVisibilityLevel" and @value="${value}"]`,
  )
  if (!desiredOption) {
    throw new Error(`No photo visibility selection found for ${value}`)
  }
  await desiredOption.click()
}

function isShowProfilePhotoFulfilled(value: SettingValue) {
  return asStrictOrStricterThan(value, TARGET_VALUE)
}

// Settings property functions
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(
    key,
    (value) => mapSettingValuesToLabels[value] || 'Other',
  )

const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, isShowProfilePhotoFulfilled)

const getCantUndoFn = () => defaultCantUndoFn

function getIsDisabledFn(settingsKey: string) {
  return (automationErrors) => {
    const error = automationErrors?.[settingsKey]?.gatherError

    if (error) {
      if (error.name === LinkedinShowProfilePhotoErrorName.NO_PROFILE_PHOTO) {
        return true
      }
    }

    return false
  }
}
function getIsDisabledReasonFn(settingsKey: string) {
  return (automationErrors) => {
    const error = automationErrors?.[settingsKey]?.gatherError

    if (error) {
      if (error.name === LinkedinShowProfilePhotoErrorName.NO_PROFILE_PHOTO) {
        return 'No profile picture - all set!'
      }
    }

    return undefined
  }
}

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'GATHER_LINKEDIN_SHOW_PROFILE_PHOTO_START',
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: homeFeedUrl,
    action: async () => {},
  },
  {
    type: StageType.ACTION,
    key: 'GATHER_LINKEDIN_SHOW_PROFILE_PHOTO',
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: profileSettingsUrl,
    action: async (_machineContext, userContext) => {
      const { updateStoredAutomationErrors, updateStoredInitialValues } =
        userContext

      let currentValue
      try {
        currentValue = await getCurrentPhotoVisibilitySelection()
      } catch (e) {
        if (e.name === LinkedinShowProfilePhotoErrorName.NO_PROFILE_PHOTO) {
          await updateStoredAutomationErrors(
            {
              [LinkedinSettingKey.SHOW_PROFILE_PHOTO]: {
                gatherError: {
                  message: e.message,
                  name: e.name,
                },
              },
            },
            LinkedinRecommendationKey.LINKEDIN_SHOW_PROFILE_PHOTO,
          )
        } else {
          throw e
        }
      }

      await updateStoredInitialValues(
        {
          [LinkedinSettingKey.SHOW_PROFILE_PHOTO]: currentValue,
        },
        LinkedinRecommendationKey.LINKEDIN_SHOW_PROFILE_PHOTO,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'UPDATE_LINKEDIN_SHOW_PROFILE_PHOTO_START',
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: homeFeedUrl,
    action: async () => {},
  },
  {
    type: StageType.ACTION,
    key: 'UPDATE_LINKEDIN_SHOW_PROFILE_PHOTO',
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: profileSettingsUrl,
    action: async (_machineContext, userContext) => {
      const { isDry, settingsSelected, updateStoredAutomationErrors } =
        userContext

      const isSelected = settingsSelected[LinkedinSettingKey.SHOW_PROFILE_PHOTO]
      if (!isSelected) {
        return
      }

      let currentValue
      try {
        currentValue = await getCurrentPhotoVisibilitySelection()
      } catch (e) {
        await updateStoredAutomationErrors(
          {
            [LinkedinSettingKey.SHOW_PROFILE_PHOTO]: {
              updateError: {
                message: e.message,
                name: e.name,
              },
            },
          },
          LinkedinRecommendationKey.LINKEDIN_SHOW_PROFILE_PHOTO,
        )
        throw e
      }

      const isFulfilled = isShowProfilePhotoFulfilled(currentValue)
      if (isFulfilled || isDry) {
        return
      }

      await setPhotoVisibilitySelection(TARGET_VALUE)
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'UNDO_LINKEDIN_SHOW_PROFILE_PHOTO_START',
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: homeFeedUrl,
    action: async () => {},
  },
  {
    type: StageType.ACTION,
    key: 'UNDO_LINKEDIN_SHOW_PROFILE_PHOTO',
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: profileSettingsUrl,
    action: async (_machineContext, userContext) => {
      const { isDry, initialValues, settingsSelected } = userContext

      const isSelected = settingsSelected[LinkedinSettingKey.SHOW_PROFILE_PHOTO]
      if (!isSelected) {
        return
      }

      const initialValue = initialValues[LinkedinSettingKey.SHOW_PROFILE_PHOTO]
      if (!initialValue) {
        return
      }

      const currentValue = await getCurrentPhotoVisibilitySelection()
      const nothingToDo = initialValue === currentValue
      if (nothingToDo || isDry) {
        return
      }

      await setPhotoVisibilitySelection(initialValue)
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
